import { User } from "firebase/auth";
import React from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useIsMobile } from "../../hooks/useIsMobile";
import { fetchMetrics, fetchFunnel, SiteMetrics, Funnel, WeeklyBucket } from "../../api/admin";
import { card, panelHeading } from "../../styles";

const tooltipStyle = {
  background: "var(--color-surface)",
  border: "1px solid var(--color-border)",
  borderRadius: 8,
  fontSize: "0.78rem",
};

function weekLabel(isoDate: string): string {
  return new Date(`${isoDate}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function WeeklyTrendChart({ title, data, gradientId, tooltipLabel }: {
  title: string; data: WeeklyBucket[]; gradientId: string; tooltipLabel: string;
}) {
  const chartData = data.map((b) => ({ name: weekLabel(b.weekStart), count: b.count }));
  return (
    <div style={{ flex: 1, minWidth: 260 }}>
      <div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-text-dim)", marginBottom: "0.5rem" }}>
        {title}
      </div>
      <ResponsiveContainer width="100%" height={120}>
        <BarChart data={chartData} margin={{ top: 4, right: 4, bottom: 4, left: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--color-accent)" stopOpacity={0.9} />
              <stop offset="95%" stopColor="var(--color-accent)" stopOpacity={0.5} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 9, fill: "var(--color-text-dim)" }} axisLine={false} tickLine={false} interval={1} />
          <YAxis hide domain={[0, "auto"]} allowDecimals={false} />
          <Tooltip contentStyle={tooltipStyle} formatter={(value: any) => [value, tooltipLabel]} />
          <Bar dataKey="count" fill={`url(#${gradientId})`} radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

interface Props {
  user: User;
}

export default function MetricsPage({ user }: Props) {
  const [metrics, setMetrics] = React.useState<SiteMetrics | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [funnel, setFunnel] = React.useState<Funnel | null>(null);
  const [funnelError, setFunnelError] = React.useState("");
  const isMobile = useIsMobile();

  React.useEffect(() => {
    fetchMetrics(user)
      .then(setMetrics)
      .catch(() => setError("Failed to load metrics"))
      .finally(() => setLoading(false));
    // Independent of the metrics fetch above — a slower or failing funnel query shouldn't
    // block the stats that already render fine without it.
    fetchFunnel(user)
      .then(setFunnel)
      .catch(() => setFunnelError("Failed to load funnel"));
  }, [user]);

  if (loading) return <div style={{ padding: "2rem", color: "var(--color-text-muted)" }}>Loading…</div>;
  if (error) return <div style={{ padding: "2rem", color: "var(--color-danger)" }}>{error}</div>;
  if (!metrics) return null;

  type Status = "normal" | "accent" | "warning" | "danger";
  const statCard = (label: string, value: number | string, sub?: string, status: Status = "normal") => {
    const valueColor =
      status === "danger"  ? "var(--color-danger)"  :
      status === "warning" ? "oklch(0.80 0.15 55)"  :
      status === "accent"  ? "var(--color-accent)"  :
      "var(--color-text)";
    const borderColor =
      status === "danger"  ? "var(--color-danger)"  :
      status === "warning" ? "oklch(0.80 0.15 55)"  :
      "var(--color-border-subtle)";
    return (
      <div style={{ background: "var(--color-surface-alt)", border: `1px solid ${borderColor}`, borderRadius: 10, padding: "1rem 1.25rem", minWidth: isMobile ? 100 : 130 }}>
        <div style={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--color-text-dim)", marginBottom: "0.5rem" }}>{label}</div>
        <div style={{ fontSize: "2rem", fontWeight: 700, lineHeight: 1, color: valueColor }}>{value}</div>
        {sub && <div style={{ fontSize: "0.72rem", color: "var(--color-text-dim)", marginTop: "0.35rem" }}>{sub}</div>}
      </div>
    );
  };

  const { registrationCap } = metrics.users;
  const capUsed = registrationCap - metrics.users.registrationCapRemaining;
  const capPct = Math.round((capUsed / Math.max(registrationCap, 1)) * 100);
  const capStatus: Status = capPct >= 90 ? "danger" : capPct >= 70 ? "warning" : "normal";

  const section = (title: string, children: React.ReactNode) => (
    <div style={{ ...card }}>
      <h2 style={panelHeading}>{title}</h2>
      <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>{children}</div>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      {section("Users", <>
        {statCard("Total", metrics.users.total, "registered accounts", "accent")}
        {statCard("Verified", metrics.users.verified, "verified email address")}
        {statCard("Unverified", metrics.users.unverified, "email not yet confirmed", metrics.users.unverified > 20 ? "warning" : "normal")}
        {statCard("Registration cap", `${capUsed} / ${registrationCap}`, `${capPct}% used · ${metrics.users.registrationCapRemaining} remaining`, capStatus)}
        {statCard("Waitlist", metrics.waitlist.pending, "waiting for a signup spot", metrics.waitlist.pending > 0 ? "warning" : "normal")}
      </>)}

      <div style={{ ...card }}>
        <h2 style={panelHeading}>Growth (last 12 weeks)</h2>
        <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
          <WeeklyTrendChart title="Signups / week" data={metrics.trends.weeklySignups} gradientId="metrics-signups-grad" tooltipLabel="Signups" />
          <WeeklyTrendChart title="Leagues created / week" data={metrics.trends.weeklyLeagues} gradientId="metrics-leagues-grad" tooltipLabel="Leagues" />
        </div>
      </div>

      {/* Leagues — overview */}
      {section("Leagues", <>
        {statCard("Total", metrics.leagues.total, "all leagues", "accent")}
        {statCard("Running", metrics.leagues.running, "started, not yet completed")}
        {statCard("Completed", metrics.leagues.completed, "all GWs finished")}
        {statCard("Pre-season", metrics.leagues.total - metrics.leagues.running - metrics.leagues.completed, "not yet started")}
      </>)}

      {/* Leagues — by game mode */}
      <div style={{ ...card }}>
        <h2 style={panelHeading}>Leagues by Game Mode</h2>
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
          {metrics.leagues.byMode.map(({ mode, count }) => (
            statCard(mode.replace("_", " ").replace("GAMEWEEK CHALLENGE", "GW CHALLENGE"), count, `${Math.round((count / Math.max(metrics.leagues.total, 1)) * 100)}% of all leagues`)
          ))}
        </div>
      </div>

      {/* Leagues — by competition */}
      <div style={{ ...card }}>
        <h2 style={panelHeading}>Leagues by Competition</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {metrics.leagues.byCompetition.map(({ competition, count }, i, arr) => (
            <div key={competition} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.6rem 0", borderBottom: i < arr.length - 1 ? "1px solid var(--color-border-subtle)" : "none" }}>
              <span style={{ fontSize: "0.88rem", color: "var(--color-text)" }}>{competition}</span>
              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                <div style={{ width: 100, height: 6, borderRadius: 3, background: "var(--color-border)", overflow: "hidden" }}>
                  <div style={{ width: `${Math.round((count / Math.max(metrics.leagues.total, 1)) * 100)}%`, height: "100%", background: "var(--color-accent)", borderRadius: 3 }} />
                </div>
                <span style={{ fontSize: "0.85rem", fontWeight: 700, color: "var(--color-accent)", minWidth: 24, textAlign: "right" }}>{count}</span>
              </div>
            </div>
          ))}
          {metrics.leagues.byCompetition.length === 0 && (
            <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--color-text-dim)", fontStyle: "italic" }}>No leagues yet.</p>
          )}
        </div>
      </div>

      {section("League membership", <>
        {statCard("Total", metrics.members.total, "league memberships", "accent")}
        {statCard("Registered", metrics.members.total - metrics.members.guests, "linked to user accounts")}
        {statCard("Guests", metrics.members.guests, "unlinked guest slots")}
      </>)}

      {/* Funnel — where did a signup come from, and did it turn into real usage. Loads
          independently of the stats above; a slow/failing query here shouldn't block them. */}
      <div style={{ ...card }}>
        <h2 style={panelHeading}>Signups by Source</h2>
        {funnelError && <p style={{ margin: 0, color: "var(--color-danger)", fontSize: "0.85rem" }}>{funnelError}</p>}
        {!funnelError && !funnel && <p style={{ margin: 0, color: "var(--color-text-dim)", fontSize: "0.85rem" }}>Loading…</p>}
        {funnel && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
              <thead>
                <tr style={{ textAlign: "left", color: "var(--color-text-dim)", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  <th style={{ padding: "0.4rem 0.6rem" }}>Source</th>
                  <th style={{ padding: "0.4rem 0.6rem" }}>Signups</th>
                  <th style={{ padding: "0.4rem 0.6rem" }}>Created a league</th>
                  <th style={{ padding: "0.4rem 0.6rem" }}>Joined a league</th>
                  <th style={{ padding: "0.4rem 0.6rem" }}>Submitted a pick</th>
                  <th style={{ padding: "0.4rem 0.6rem" }}>Median hrs to 1st league</th>
                </tr>
              </thead>
              <tbody>
                {funnel.bySource.map((row) => (
                  <tr key={row.source} style={{ borderTop: "1px solid var(--color-border-subtle)" }}>
                    <td style={{ padding: "0.5rem 0.6rem", fontWeight: 600, color: "var(--color-text)" }}>{row.source}</td>
                    <td style={{ padding: "0.5rem 0.6rem" }}>{row.signups}</td>
                    <td style={{ padding: "0.5rem 0.6rem" }}>{row.createdLeaguePct}%</td>
                    <td style={{ padding: "0.5rem 0.6rem" }}>{row.joinedLeaguePct}%</td>
                    <td style={{ padding: "0.5rem 0.6rem" }}>{row.submittedPickPct}%</td>
                    <td style={{ padding: "0.5rem 0.6rem" }}>{row.medianHoursToFirstLeague === null ? "—" : Math.round(row.medianHoursToFirstLeague)}</td>
                  </tr>
                ))}
                {funnel.bySource.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: "0.75rem 0.6rem", color: "var(--color-text-dim)", fontStyle: "italic" }}>No signups yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {funnel && funnel.referralBreakdown.length > 0 && (
        <div style={{ ...card }}>
          <h2 style={panelHeading}>Top Referrers</h2>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
              <thead>
                <tr style={{ textAlign: "left", color: "var(--color-text-dim)", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  <th style={{ padding: "0.4rem 0.6rem" }}>Referrer</th>
                  <th style={{ padding: "0.4rem 0.6rem" }}>Referred</th>
                  <th style={{ padding: "0.4rem 0.6rem" }}>Activated</th>
                </tr>
              </thead>
              <tbody>
                {funnel.referralBreakdown.map((row) => (
                  <tr key={row.referrerUserId} style={{ borderTop: "1px solid var(--color-border-subtle)" }}>
                    <td style={{ padding: "0.5rem 0.6rem", color: "var(--color-text)" }}>{row.referrerDisplayName ?? row.referrerEmail ?? row.referrerUserId}</td>
                    <td style={{ padding: "0.5rem 0.6rem" }}>{row.totalReferred}</td>
                    <td style={{ padding: "0.5rem 0.6rem", fontWeight: 700, color: "var(--color-accent)" }}>{row.activatedReferred}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
